/**
 * Skyyrizon Product — PDP controller.
 *  - Thumbnail swap
 *  - Variant picker
 *  - Add to Cart + Buy It Now
 *  - Post-click upsell modal powered by Shopify /recommendations/products.json
 *  - Bundle tier logic (1 / 2 / 3 items) — tier choice drives main-product quantity
 *  - 15-min countdown timer on tier-3 upsell picks
 *
 * Bundle tiers (quantity of the main product added when tapping Add to Cart / Buy It Now):
 *   Tier 1 → 1 unit. No discount. Upsell modal shows 1 related pick.
 *   Tier 2 → 2 units. Shopify automatic "Bundle — 10% off 2 items" fires at checkout.
 *            Upsell modal shows 2 related picks as optional extras.
 *   Tier 3 → 3 units. Shopify automatic "Bundle — 15% off 3 items" fires at checkout.
 *            Upsell modal shows 3 related picks (1 cheap + 2 premium) as optional extras.
 *            When a premium pick is added, SKYYURGENT10 code is silently applied.
 */
(function () {
  'use strict';

  var TIMER_KEY_PREFIX = 'sky_tier3_timer_';

  // ── Money formatting ──────────────────────────────────────────

  function formatMoney(cents) {
    var format = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimal = Math.round(cents / 100);
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimal)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(amountNoDecimal));
  }

  // ── Cart API helpers ──────────────────────────────────────────

  function cartAdd(variantId, quantity) {
    var routes = (window.Shopify && window.Shopify.routes) || {};
    var base = routes.cart_add_url || '/cart/add.js';
    // Request cart-icon-bubble section in the response so we can update the header counter.
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'sections=cart-icon-bubble,cart-live-region-text';
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = {};
        if (text) {
          try { data = JSON.parse(text); } catch (e) { data = { description: text }; }
        }
        if (!res.ok) throw (data && Object.keys(data).length ? data : { description: 'Request failed (' + res.status + ')' });
        return data;
      });
    });
  }

  function fetchCart() {
    var routes = (window.Shopify && window.Shopify.routes) || {};
    var root = routes.root || '/';
    return fetch(root + 'cart.js', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : { item_count: 0 }; })
      .catch(function () { return { item_count: 0 }; });
  }

  // Silent discount code apply — calls /discount/CODE which sets session cookie
  function applyDiscountCode(code) {
    if (!code) return Promise.resolve();
    var url = '/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js';
    return fetch(url, { credentials: 'include' }).catch(function () { /* non-fatal */ });
  }

  // Replace a section's DOM from Shopify's section-render response HTML.
  function replaceSectionOnPage(sectionKey, newHtml) {
    if (!newHtml) return;
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(newHtml, 'text/html');
      var newSectionEl = doc.querySelector('#shopify-section-' + sectionKey);
      var currentSectionEl = document.getElementById('shopify-section-' + sectionKey);
      if (newSectionEl && currentSectionEl) {
        currentSectionEl.replaceWith(newSectionEl);
        return;
      }
      // Fallback: locate by class (Dawn's cart icon bubble)
      var currentBubble = document.querySelector('.cart-count-bubble');
      var newBubble = doc.querySelector('.cart-count-bubble');
      if (currentBubble && newBubble) {
        currentBubble.replaceWith(newBubble);
      } else if (newBubble) {
        // Bubble didn't exist before (cart was empty); inject it into cart icon
        var cartIcon = document.querySelector('a.header__icon--cart, a[href="/cart"], .cart-count-bubble')?.parentNode;
        if (cartIcon) cartIcon.appendChild(newBubble);
      }
    } catch (e) { /* non-fatal */ }
  }

  // Primary cart-icon refresher: swaps the cart-icon-bubble section HTML
  function updateCartIconFromResponse(cartResponse) {
    if (cartResponse && cartResponse.sections) {
      if (cartResponse.sections['cart-icon-bubble']) {
        replaceSectionOnPage('cart-icon-bubble', cartResponse.sections['cart-icon-bubble']);
      }
      if (cartResponse.sections['cart-live-region-text']) {
        replaceSectionOnPage('cart-live-region-text', cartResponse.sections['cart-live-region-text']);
      }
    }
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'skyyrizon-pdp', cartData: cartResponse });
    }
  }

  // Legacy name kept so other callers (if any) still work — routes to the new updater
  function refreshCartBubble(cart) { updateCartIconFromResponse(cart); }

  function fetchRecommendations(productId, limit) {
    return fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + (limit || 12) + '&intent=related', {
      credentials: 'include',
    })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) { return (data && data.products) || []; })
      .catch(function () { return []; });
  }

  // ── Countdown timer (shared across tier-3 upsells) ────────────

  function getOrCreateTimerExpiry(productId, durationMs) {
    var key = TIMER_KEY_PREFIX + productId;
    var raw = window.localStorage && window.localStorage.getItem(key);
    var expiry = raw ? parseInt(raw, 10) : 0;
    var now = Date.now();
    if (!expiry || isNaN(expiry) || expiry <= now) {
      expiry = now + durationMs;
      try { window.localStorage.setItem(key, String(expiry)); } catch (e) { /* ignore */ }
    }
    return expiry;
  }

  function formatCountdown(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var mins = Math.floor(total / 60);
    var secs = total % 60;
    return mins + ':' + (secs < 10 ? '0' + secs : secs);
  }

  // ── PDP init ─────────────────────────────────────────────────

  function initPDP(section) {
    var variantsScript = section.querySelector('[data-sky-pdp-variants]');
    var variants = [];
    if (variantsScript) {
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }
    }

    var form = section.querySelector('#sky-pdp-form');
    var idInput = section.querySelector('[data-sky-pdp-id]');
    var priceEl = section.querySelector('[data-sky-pdp-price]');
    var compareEl = section.querySelector('[data-sky-pdp-compare]');
    var saveEl = section.querySelector('[data-sky-pdp-save]');
    var addBtn = section.querySelector('[data-sky-pdp-add]');
    var buyBtn = section.querySelector('[data-sky-pdp-buy-now]');
    var mainImg = section.querySelector('[data-sky-pdp-main-image]');
    var thumbs = section.querySelectorAll('[data-sky-pdp-thumb]');
    var selects = section.querySelectorAll('[data-sky-pdp-option-index]');

    var originalAddText = addBtn ? addBtn.textContent.trim() : 'Add to Cart';
    var originalBuyText = buyBtn ? buyBtn.textContent.trim() : 'Buy It Now';

    // Upsell elements
    var upsellRoot = section.querySelector('[data-sky-pdp-upsell]');
    var upsellProductsEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-products]') : null;
    var upsellEmptyEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-empty]') : null;
    var upsellBundleEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-bundle]') : null;
    var upsellBundleLabelEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-bundle-label]') : null;
    var upsellBundleTimerEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-bundle-timer]') : null;
    var upsellBundleCountdownEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-bundle-countdown]') : null;
    var upsellTierEls = upsellRoot ? upsellRoot.querySelectorAll('[data-sky-pdp-upsell-tier]') : [];
    var upsellSkipBtn = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-skip]') : null;
    var upsellDismissBtns = upsellRoot ? upsellRoot.querySelectorAll('[data-sky-pdp-upsell-dismiss]') : [];
    var upsellTitleEl = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-title]') : null;
    var upsellTemplate = upsellRoot ? upsellRoot.querySelector('[data-sky-pdp-upsell-card-template]') : null;

    var productId = upsellRoot ? upsellRoot.getAttribute('data-product-id') : null;
    var bundle2Percent = upsellRoot ? parseInt(upsellRoot.getAttribute('data-bundle-2-percent'), 10) : 10;
    var bundle3Percent = upsellRoot ? parseInt(upsellRoot.getAttribute('data-bundle-3-percent'), 10) : 15;
    var cheapThresholdCents = upsellRoot ? parseInt(upsellRoot.getAttribute('data-cheap-cents'), 10) : 2500;
    var timerMinutes = upsellRoot ? parseInt(upsellRoot.getAttribute('data-timer-minutes'), 10) : 15;
    var tier3DiscountPercent = upsellRoot ? parseInt(upsellRoot.getAttribute('data-tier3-discount-percent'), 10) : 10;

    var upsellOrigin = null;   // 'cart' | 'buy_now'
    var recommendations = [];  // fetched at page load
    var timerInterval = null;
    var selectedTier = 1;      // user's chosen bundle tier (1|2|3), default 1
    var userPickedTier = false; // true once user explicitly clicks a tier button
    var premiumAddedThisSession = false; // flips to true when a tier-3 premium pick is added

    var urgentDiscountCode = upsellRoot ? upsellRoot.getAttribute('data-urgent-discount-code') : '';

    // NOTE: Code-based bundle discounts (SKYYBUNDLE10 / SKYYBUNDLE15) were rolled back
    // in favor of Shopify's quantity-based automatic discounts. The schema fields
    // `bundle_2_discount_code` / `bundle_3_discount_code` still exist but are not wired.
    // To re-enable: restore the applyBundleTierDiscount function and call it after
    // cartAdd in both the Add to Cart form submit and the Buy It Now handler.

    // ── Bundle selector ───────────────────────────

    var bundleButtons = section.querySelectorAll('[data-sky-bundle-tier]');
    var bundleMathEl = section.querySelector('[data-sky-pdp-bundles-math]');
    var bundle2PercentPDP = bundleMathEl ? parseInt(bundleMathEl.getAttribute('data-bundle-2-percent'), 10) || 10 : 10;
    var bundle3PercentPDP = bundleMathEl ? parseInt(bundleMathEl.getAttribute('data-bundle-3-percent'), 10) || 15 : 15;

    function updateBundleMath() {
      if (!bundleMathEl) return;
      var v = findVariant();
      var unit = (v && typeof v.price === 'number')
        ? v.price
        : parseInt(bundleMathEl.getAttribute('data-unit-cents'), 10);
      if (!unit || isNaN(unit)) { bundleMathEl.innerHTML = ''; return; }
      if (selectedTier <= 1) {
        bundleMathEl.innerHTML = '<span class="sky-pdp__bundles-math-single">1 &times; ' + formatMoney(unit) + ' &middot; Standard pricing</span>';
      } else {
        var pct = selectedTier === 2 ? bundle2PercentPDP : bundle3PercentPDP;
        var subtotal = unit * selectedTier;
        var discounted = Math.round(subtotal * (100 - pct) / 100);
        bundleMathEl.innerHTML =
          '<span class="sky-pdp__bundles-math-row">' +
            '<span class="sky-pdp__bundles-math-calc">' + selectedTier + ' &times; ' + formatMoney(unit) + '</span>' +
            '<s class="sky-pdp__bundles-math-was">' + formatMoney(subtotal) + '</s>' +
            '<strong class="sky-pdp__bundles-math-now">' + formatMoney(discounted) + '</strong>' +
          '</span>' +
          '<span class="sky-pdp__bundles-math-note">Save ' + pct + '% &middot; applied at checkout</span>';
      }
      if (addBtn && !addBtn.disabled) {
        var base = originalAddText;
        addBtn.textContent = selectedTier > 1 ? ('Add ' + selectedTier + ' to Cart') : base;
      }
    }

    Array.prototype.forEach.call(bundleButtons, function (btn) {
      btn.addEventListener('click', function () {
        var tier = parseInt(btn.getAttribute('data-sky-bundle-tier'), 10) || 1;
        selectedTier = tier;
        userPickedTier = true;
        Array.prototype.forEach.call(bundleButtons, function (b) {
          var isSel = b === btn;
          b.classList.toggle('is-selected', isSel);
          b.setAttribute('aria-pressed', isSel ? 'true' : 'false');
        });
        updateBundleMath();
      });
    });

    // ── Variant matching ───────────────────────────

    function selectedOptions() {
      return Array.prototype.map.call(selects, function (s) { return s.value; });
    }

    function findVariant() {
      var opts = selectedOptions();
      return variants.find(function (v) {
        return (v.option1 == null || v.option1 === opts[0])
            && (v.option2 == null || v.option2 === opts[1])
            && (v.option3 == null || v.option3 === opts[2]);
      });
    }

    function updateVariant() {
      var v = findVariant();
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.innerHTML = formatMoney(v.price);
      if (compareEl && saveEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.innerHTML = formatMoney(v.compare_at_price);
          compareEl.style.display = '';
          var pct = Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100);
          saveEl.textContent = 'Save ' + pct + '%';
          saveEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
          saveEl.style.display = 'none';
        }
      }
      if (addBtn) {
        if (v.available) { addBtn.disabled = false; addBtn.textContent = originalAddText; }
        else { addBtn.disabled = true; addBtn.textContent = 'Sold Out'; }
      }
      if (buyBtn) buyBtn.disabled = !v.available;
      if (v.featured_image && mainImg) {
        var src = v.featured_image.src || v.featured_image;
        mainImg.src = src.replace(/(\.[a-z]{3,4})(\?.*)?$/i, '_1400x$1');
      }
    }

    Array.prototype.forEach.call(selects, function (s) {
      s.addEventListener('change', function () { updateVariant(); updateBundleMath(); });
    });

    // Initial math render (reflects current variant + default tier)
    updateBundleMath();

    // ── Thumbnail swap ─────────────────────────────

    Array.prototype.forEach.call(thumbs, function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var fullSrc = t.getAttribute('data-full-src');
        if (fullSrc && mainImg) mainImg.src = fullSrc;
        Array.prototype.forEach.call(thumbs, function (x) { x.classList.remove('is-active'); });
        t.classList.add('is-active');
      });
    });

    // ── Upsell: prefetch recommendations on page load ──

    if (upsellRoot && productId) {
      // Fetch a larger pool (20) so random picks have real variety each refresh
      fetchRecommendations(productId, 20).then(function (products) {
        recommendations = products.filter(function (p) {
          return p.available !== false && p.price > 0;
        });
      });
    }

    // ── Upsell: tier-based product selection ──────

    // Pick a random item from a price-bucket range [startFrac..endFrac)
    // Uses a sorted-by-price copy of the pool, slices a bucket, returns a random pick.
    function pickFromBucket(sortedPool, startFrac, endFrac, excludeIds) {
      if (!sortedPool.length) return null;
      var start = Math.floor(sortedPool.length * startFrac);
      var end = Math.max(start + 1, Math.ceil(sortedPool.length * endFrac));
      var bucket = sortedPool.slice(start, Math.min(end, sortedPool.length));
      // Remove excluded ids so we don't pick the same product twice
      if (excludeIds && excludeIds.length) {
        bucket = bucket.filter(function (p) { return excludeIds.indexOf(p.id) === -1; });
      }
      if (!bucket.length) return null;
      return bucket[Math.floor(Math.random() * bucket.length)];
    }

    function pickTierProducts(tier, pool) {
      var sorted = pool.slice().sort(function (a, b) { return a.price - b.price; });
      var selected = [];
      var usedIds = [];

      function push(pick, extras) {
        if (!pick) return;
        selected.push(Object.assign({}, pick, extras || {}));
        usedIds.push(pick.id);
      }

      if (tier === 1) {
        // 1 random pick from bottom-35% price bucket (varies each refresh)
        push(pickFromBucket(sorted, 0, 0.35, usedIds), { tier: 1, role: 'cheap' });
        // Fallback if pool too small
        if (selected.length === 0 && sorted[0]) push(sorted[0], { tier: 1 });
      } else if (tier === 2) {
        // 1 cheap + 1 mid, both randomized within their buckets
        push(pickFromBucket(sorted, 0, 0.4, usedIds), { tier: 2, role: 'cheap' });
        push(pickFromBucket(sorted, 0.4, 0.75, usedIds), { tier: 2, role: 'mid' });
        if (selected.length === 0 && sorted[0]) push(sorted[0], { tier: 2 });
      } else {
        // Tier 3: 1 cheap under threshold + 2 premium from top bucket
        var cheapCandidates = sorted.filter(function (p) { return p.price <= cheapThresholdCents; });
        if (cheapCandidates.length > 0) {
          push(cheapCandidates[Math.floor(Math.random() * cheapCandidates.length)], { tier: 3, role: 'cheap' });
        } else {
          push(pickFromBucket(sorted, 0, 0.35, usedIds), { tier: 3, role: 'cheap' });
        }
        // 2 random premium from top-35% price bucket
        push(pickFromBucket(sorted, 0.65, 1.0, usedIds), { tier: 3, role: 'premium' });
        push(pickFromBucket(sorted, 0.65, 1.0, usedIds), { tier: 3, role: 'premium' });
        // Fallback: if we didn't get 2 premiums, fill with anything expensive
        if (selected.filter(function (x) { return x.role === 'premium'; }).length < 2) {
          var reverseSorted = sorted.slice().reverse();
          for (var i = 0; i < reverseSorted.length && selected.length < 3; i++) {
            if (usedIds.indexOf(reverseSorted[i].id) === -1) {
              push(reverseSorted[i], { tier: 3, role: 'premium' });
            }
          }
        }
      }

      return selected;
    }

    // ── Upsell: render cards into modal ───────────

    function renderUpsellCards(products) {
      if (!upsellProductsEl || !upsellTemplate) return;
      upsellProductsEl.innerHTML = '';

      if (!products || products.length === 0) {
        if (upsellEmptyEl) upsellEmptyEl.hidden = false;
        return;
      }
      if (upsellEmptyEl) upsellEmptyEl.hidden = true;

      products.forEach(function (p) {
        var clone = upsellTemplate.content.cloneNode(true);
        var card = clone.querySelector('[data-sky-pdp-upsell-card]');
        var links = clone.querySelectorAll('[data-sky-upsell-link]');
        var img = clone.querySelector('[data-sky-upsell-img]');
        var name = clone.querySelector('[data-sky-upsell-name]');
        var price = clone.querySelector('[data-sky-upsell-price]');
        var deal = clone.querySelector('[data-sky-upsell-deal]');
        var btn = clone.querySelector('[data-sky-pdp-upsell-add]');

        // Links
        Array.prototype.forEach.call(links, function (l) { l.setAttribute('href', p.url); });

        // Image
        if (img && p.featured_image) {
          img.src = p.featured_image + (p.featured_image.indexOf('?') === -1 ? '?width=240' : '&width=240');
          img.alt = p.title;
        }

        // Name
        if (name) name.textContent = p.title;

        // Price — only show real compare-at discounts; no synthetic premium strikethrough.
        if (price) {
          if (p.compare_at_price && p.compare_at_price > p.price) {
            price.innerHTML = '<span class="sky-pdp-upsell__price-sale">' + formatMoney(p.price) + '</span>' +
                              '<s class="sky-pdp-upsell__price-compare">' + formatMoney(p.compare_at_price) + '</s>';
          } else {
            price.innerHTML = formatMoney(p.price);
          }
        }

        // Deal badge — suppressed; we no longer promise a tier-3 % that doesn't surface in cart.
        if (deal) deal.hidden = true;

        // Add button variant id
        if (btn && p.variants && p.variants[0]) {
          btn.setAttribute('data-variant-id', p.variants[0].id);
          btn.setAttribute('aria-label', 'Add ' + p.title + ' to cart');
          wireUpsellAddBtn(btn, p);
        }

        upsellProductsEl.appendChild(clone);
      });
    }

    function wireUpsellAddBtn(btn, productData) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        var labelDefault = btn.querySelector('[data-sky-pdp-upsell-btn-default]');
        var labelLoading = btn.querySelector('[data-sky-pdp-upsell-btn-loading]');
        var labelDone    = btn.querySelector('[data-sky-pdp-upsell-btn-done]');

        if (labelDefault) labelDefault.hidden = true;
        if (labelLoading) labelLoading.hidden = false;
        btn.disabled = true;

        cartAdd(variantId, 1)
          .then(function (cartResponse) {
            if (labelLoading) labelLoading.hidden = true;
            if (labelDone) labelDone.hidden = false;

            // Update header cart icon immediately using the section HTML returned
            updateCartIconFromResponse(cartResponse);

            // If this was a premium tier-3 pick, silently apply the urgent discount code
            if (productData && productData.role === 'premium' && urgentDiscountCode) {
              premiumAddedThisSession = true;
              applyDiscountCode(urgentDiscountCode);
            }
          })
          .catch(function (err) {
            if (labelLoading) labelLoading.hidden = true;
            if (labelDefault) labelDefault.hidden = false;
            btn.disabled = false;
            var msg = (err && err.description) || (err && err.message) || 'Could not add.';
            window.alert(msg);
          });
      });
    }

    // ── Upsell: bundle banner + timer ─────────────

    function updateBundleBanner(tier) {
      if (!upsellBundleEl) return;
      // If this product has no bundle selector (e.g., not in Bundle Eligible collection),
      // hide the bundle banner in the upsell modal too — nothing to promise.
      if (!bundleButtons || bundleButtons.length === 0) {
        upsellBundleEl.hidden = true;
        return;
      }
      upsellBundleEl.hidden = false;

      // Tier states (everything up to current tier is unlocked; current is active)
      Array.prototype.forEach.call(upsellTierEls, function (el) {
        el.classList.remove('is-active', 'is-unlocked');
        var tierNum = parseInt(el.getAttribute('data-sky-pdp-upsell-tier'), 10);
        if (tierNum <= tier) el.classList.add('is-unlocked');
        if (tierNum === tier) el.classList.add('is-active');
      });

      // Banner label — based on user's chosen tier
      if (upsellBundleLabelEl) {
        if (tier >= 3) {
          upsellBundleLabelEl.innerHTML = '<strong>You unlocked ' + bundle3Percent + '% off.</strong> Try these premium picks, only available now.';
        } else if (tier === 2) {
          upsellBundleLabelEl.innerHTML = '<strong>Save ' + bundle3Percent + '%</strong> when you add 1 more.';
        } else {
          upsellBundleLabelEl.innerHTML = '<strong>Save ' + bundle2Percent + '%</strong> when you add 1 more.';
        }
      }

      // Timer only at tier 3
      if (upsellBundleTimerEl) {
        if (tier >= 3) {
          upsellBundleTimerEl.hidden = false;
          startCountdown();
        } else {
          upsellBundleTimerEl.hidden = true;
          stopCountdown();
        }
      }
    }

    function startCountdown() {
      if (!upsellBundleCountdownEl || !productId) return;
      stopCountdown();
      var expiry = getOrCreateTimerExpiry(productId, timerMinutes * 60 * 1000);
      function tick() {
        var remaining = expiry - Date.now();
        if (remaining <= 0) {
          upsellBundleCountdownEl.textContent = '0:00';
          stopCountdown();
          // Hide tier-3 deal badges (offer expired)
          var badges = upsellRoot ? upsellRoot.querySelectorAll('[data-sky-upsell-deal]') : [];
          Array.prototype.forEach.call(badges, function (b) { b.hidden = true; });
          return;
        }
        upsellBundleCountdownEl.textContent = formatCountdown(remaining);
      }
      tick();
      timerInterval = window.setInterval(tick, 1000);
    }

    function stopCountdown() {
      if (timerInterval) { window.clearInterval(timerInterval); timerInterval = null; }
    }

    // ── Upsell: show / hide modal ─────────────────

    function showUpsell(origin) {
      if (!upsellRoot) return false;

      upsellOrigin = origin;
      upsellRoot.hidden = false;
      upsellRoot.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      if (upsellSkipBtn) {
        upsellSkipBtn.textContent = origin === 'buy_now' ? 'No thanks, continue to cart' : 'No thanks';
      }

      // Tier is the user's explicit bundle choice. If they never clicked a tier,
      // show tier 1 — we don't silently promote based on prior cart quantity.
      fetchCart().then(function (cart) {
        var tier = userPickedTier ? selectedTier : 1;

        updateBundleBanner(tier);

        var picks = pickTierProducts(tier, recommendations);
        renderUpsellCards(picks);

        // Set a tier-aware title
        if (upsellTitleEl) {
          upsellTitleEl.textContent = tier >= 3
            ? 'Only available now — premium picks'
            : 'Customers also loved these';
        }
      });

      return true;
    }

    function hideUpsell() {
      if (!upsellRoot) return;
      upsellRoot.hidden = true;
      upsellRoot.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      stopCountdown();
    }

    function finishFlow() {
      if (upsellOrigin === 'buy_now') {
        // Buy It Now → upsell → "No thanks, continue to cart" → routes to /cart
        // (SKYYURGENT10 cookie, if set, still applies automatically at checkout.)
        window.location.href = '/cart';
      } else {
        hideUpsell();
      }
    }

    // Skip button ("No thanks" / "No thanks, continue to cart") routes per origin:
    //   · Add to Cart origin → close popup, stay on PDP
    //   · Buy It Now origin  → go to /cart (per the button's own text)
    if (upsellSkipBtn) upsellSkipBtn.addEventListener('click', finishFlow);

    // X button + backdrop + Escape = pure dismiss. Never navigates regardless of origin.
    // Customer stays on the PDP with the item already in their cart.
    Array.prototype.forEach.call(upsellDismissBtns, function (el) {
      el.addEventListener('click', hideUpsell);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && upsellRoot && !upsellRoot.hidden) hideUpsell();
    });

    // ── Add to Cart ────────────────────────────────

    if (form && addBtn) {
      form.addEventListener('submit', function (e) {
        if (!upsellRoot) return; // No upsell available, let native submit happen
        e.preventDefault();
        if (addBtn.disabled) return;

        var origText = addBtn.textContent.trim();
        addBtn.disabled = true;
        addBtn.textContent = 'Adding…';

        var vid = idInput ? idInput.value : null;
        if (!vid) { addBtn.disabled = false; addBtn.textContent = origText; return; }

        cartAdd(vid, selectedTier)
          .then(function (cartResponse) {
            updateCartIconFromResponse(cartResponse);
            addBtn.textContent = origText;
            addBtn.disabled = false;
            showUpsell('cart');
          })
          .catch(function (err) {
            addBtn.textContent = origText;
            addBtn.disabled = false;
            var msg = (err && err.description) || (err && err.message) || 'Could not add to cart.';
            window.alert(msg);
          });
      });
    }

    // ── Buy It Now ─────────────────────────────────

    if (buyBtn) {
      buyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (buyBtn.disabled) return;

        buyBtn.disabled = true;
        buyBtn.textContent = 'Processing…';

        var vid = idInput ? idInput.value : null;
        if (!vid) { buyBtn.disabled = false; buyBtn.textContent = originalBuyText; return; }

        cartAdd(vid, selectedTier)
          .then(function (cartResponse) {
            updateCartIconFromResponse(cartResponse);
            buyBtn.textContent = originalBuyText;
            buyBtn.disabled = false;

            if (upsellRoot) {
              showUpsell('buy_now');
            } else {
              window.location.href = '/checkout';
            }
          })
          .catch(function (err) {
            buyBtn.disabled = false;
            buyBtn.textContent = originalBuyText;
            var msg = (err && err.description) || (err && err.message) || 'Could not add to cart.';
            window.alert(msg);
          });
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-sky-pdp]').forEach(initPDP);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
